import React, {
    ChangeEvent,
    FocusEvent,
    KeyboardEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { TextField } from '@mui/material'
import { AnyInputProps } from './AnyInput.types'
import { AnyComponentRef } from './AnyComponent.types'
import {
    EMAIL_VALIDATOR,
    InputValidator,
    NUMBER_VALIDATOR,
    PASSWORD_VALIDATOR,
    PHONE_VALIDATOR,
    RequiredValidator,
    URL_VALIDATOR,
} from '../validators'

function AnyInput({
    type = 'text',
    variant = 'outlined',
    size = 'small',
    margin = 'dense',
    fullWidth = true,
    name,
    label,
    initialValue,
    rows,
    minRows,
    maxRows,
    maxLength,
    required,
    disabled,
    validators = [],
    hiddenLabel = false,
    startAdornment,
    endAdornment,
    register,
    notifier,
}: AnyInputProps) {
    const [blured, setBlured] = useState(false)
    const [valid, setValid] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [error, setError] = useState<string>()
    const [value, setValue] = useState<unknown>(initialValue ?? '')
    const allValidators = useMemo<InputValidator[]>(() => {
        let allValidators = [...validators]
        if (required) {
            allValidators = [new RequiredValidator(type), ...validators]
        }

        switch (type) {
            case 'email':
                return [EMAIL_VALIDATOR, ...allValidators]
            case 'tel':
                return [PHONE_VALIDATOR, ...allValidators]
            case 'password':
                return [PASSWORD_VALIDATOR, ...allValidators]
            case 'number':
                return [NUMBER_VALIDATOR, ...allValidators]
            case 'url':
                return [URL_VALIDATOR, ...allValidators]
            default:
                return allValidators
        }
    }, [required, type, validators])

    const ref = useRef<AnyComponentRef<unknown>>({
        valid,
        dirty,
        value,
        setBlur: () => setBlured(true),
    })

    const inputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value

        setDirty(true)
        if (!raw) {
            setValue('')
            return
        }

        switch (type) {
            case 'number':
                setValue(+raw)
                break
            default:
                setValue(raw)
        }
    }

    const inputBlured = (_event: FocusEvent<HTMLInputElement>) => {
        if (!blured) {
            setBlured(true)
        }
    }

    const onNotify = useCallback(
        (value?: unknown) => {
            if (notifier) {
                notifier(name, value)
            }
        },
        [notifier, name]
    )

    useEffect(() => {
        let valid = true
        let error: string | undefined
        for (const validator of allValidators) {
            if (!validator.isValid(value)) {
                valid = false
                error = validator.getMessage()
                break
            }
        }

        ref.current.valid = valid
        ref.current.dirty = dirty
        ref.current.value = value

        setValid(valid)
        setError(error)

        onNotify(value)
    }, [value, dirty, allValidators, onNotify])

    useEffect(() => {
        register(name, ref.current)
    }, [register, name, ref])

    useEffect(() => {
        setValue(initialValue ?? '')
    }, [initialValue])

    const onKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (type === 'number' && ['e', 'E', '+', '-'].includes(e.key)) {
                e.preventDefault()
            }
        },
        [type]
    )

    return (
        <TextField
            type={type}
            variant={variant}
            size={size}
            margin={margin}
            name={name}
            hiddenLabel={hiddenLabel}
            label={hiddenLabel ? undefined : label}
            value={value}
            required={required}
            disabled={disabled}
            fullWidth={fullWidth}
            rows={rows}
            minRows={minRows}
            maxRows={maxRows}
            multiline={
                (rows ?? 0) > 1 || (minRows ?? 0) > 1 || (maxRows ?? 0) > 1
            }
            inputProps={{
                maxLength: maxLength,
                placeholder: hiddenLabel ? (label as string) : undefined,
            }}
            error={blured && !valid}
            helperText={blured ? error : undefined}
            onChange={inputChanged}
            onBlur={inputBlured}
            onKeyDown={onKeyDown}
            InputProps={{
                endAdornment,
                startAdornment,
            }}
        />
    )
}

export default memo(AnyInput)
